import { ContactList } from "components/ui/ContactList";
import { Clock, Check, X } from "lucide-react";
import { usePendingContacts } from "context/PendingContactsContext";
import { useEffect } from "react";
import fetchWithCredentials from "apiClient";
import { formatTimestamp } from "utils/dateFormat";
import { contactListItemClasses } from "utils/contactListItemClasses";
import { useAuth } from "context/AuthContext";
import { ReplyMuteBadge } from "components/ReplyMuteBadge";
import type { ReplyMute } from "types/ReplyMute";

interface PendingContactsListProps {
  onSelectContact: (contactId: number) => void;
  selectedContactId: number | null;
  condoId: number;
}

interface TransformedPendingContact {
  id: number;
  pendingContactId: number;
  name: string;
  subtitle: string;
  lastMessageAt: string | null;
  hasUnread: boolean;
  replyMute?: ReplyMute | null;
}

export function PendingContactsList({
  onSelectContact,
  selectedContactId,
  condoId,
}: PendingContactsListProps) {
  const {
    pendingContacts,
    loading,
    error,
    refresh,
    markContactAsRead,
    removePendingContact,
  } = usePendingContacts();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const handleSelectContact = (contactId: number) => {
    markContactAsRead(contactId);
    onSelectContact(contactId);
  };

  const handleConfirmContact = async (
    e: React.MouseEvent,
    pendingContactId: number
  ) => {
    e.stopPropagation(); // Prevent triggering the contact selection

    // Optimistically remove the contact from the list
    removePendingContact(pendingContactId);

    try {
      await fetchWithCredentials(
        `/manager-api/pending-contacts/${pendingContactId}/confirm/?condo=${condoId}`,
        {
          method: "POST",
        }
      );
      // If successful, the contact is already removed optimistically
    } catch (err: any) {
      console.error("Error confirming contact:", err);
      // On error, refresh to restore the correct state
      refresh(condoId);
    }
  };

  const handleRejectContact = async (
    e: React.MouseEvent,
    pendingContactId: number
  ) => {
    e.stopPropagation();

    removePendingContact(pendingContactId);

    try {
      await fetchWithCredentials(
        `/manager-api/pending-contacts/${pendingContactId}/reject/?condo=${condoId}`,
        {
          method: "POST",
        }
      );
    } catch (err: any) {
      console.error("Error rejecting contact:", err);
      refresh(condoId);
    }
  };

  // Fetch pending contacts when condo changes
  useEffect(() => {
    refresh(condoId);
  }, [condoId, refresh]);

  // Transform pending contacts to include contact info at the top level
  const transformedPendingContacts: TransformedPendingContact[] =
    pendingContacts.map((pendingContact) => ({
      id: pendingContact.contact.id, // Use contact ID for selection
      pendingContactId: pendingContact.id, // Store pending contact ID for confirmation
      name: pendingContact.contact.name,
      subtitle: pendingContact.identificationDetails,
      lastMessageAt: pendingContact.lastMessageAt,
      hasUnread: pendingContact.hasUnread,
      replyMute: pendingContact.replyMute ?? null,
    }));

  return (
    <ContactList
      listId="pending-contacts"
        title={locale === "es" ? "Pendientes" : "Pending contacts"}
      icon={<Clock className="w-4 h-4 mr-2" />}
      items={transformedPendingContacts}
      loading={loading}
      error={error}
        searchPlaceholder={
          locale === "es" ? "Buscar pendientes..." : "Search pending contacts..."
        }
      selectedId={selectedContactId}
      onSelectItem={handleSelectContact}
      getItemName={(item) => item.name}
      getItemSubtitle={(item) => item.subtitle}
      renderItem={(item, isSelected) => {
        return (
          <div className={contactListItemClasses(isSelected)}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <button
                  onClick={(e) =>
                    handleConfirmContact(e, item.pendingContactId)
                  }
                  className="flex-shrink-0 p-1 rounded hover:bg-green-100 text-green-600 transition-colors"
                  title={locale === "es" ? "Confirmar contacto" : "Confirm contact"}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) =>
                    handleRejectContact(e, item.pendingContactId)
                  }
                  className="flex-shrink-0 p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                  title={locale === "es" ? "Rechazar contacto" : "Reject contact"}
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-xs lg:text-sm truncate ${
                      item.hasUnread ? "font-bold" : "font-medium"
                    }`}
                  >
                    {item.name}
                  </div>
                  {item.subtitle && (
                    <div className="text-xs text-gray-500">{item.subtitle}</div>
                  )}
                  <ReplyMuteBadge mute={item.replyMute} locale={locale} />
                </div>
              </div>
              {item.lastMessageAt && (
                <div className="text-xs text-gray-400 flex-shrink-0">
                  {formatTimestamp(item.lastMessageAt, locale)}
                </div>
              )}
            </div>
          </div>
        );
      }}
    />
  );
}
